import React, { useEffect, useCallback } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import { AddButton } from '@td-client-frontend/common';
import { DependentTable } from '../../../components/Member';
import { useMemberDetails } from '../../../hooks/useMemberDetail';
import { validationSchema } from '../../../utils/members/validationSchema';
import { useMemberContext } from '../../../context/MemberContext';
import AccountInformation from './AccountInformation';
import WithPermission from '../../../components/WithPermission';
import { Group } from '../../../@types/Members';

interface DependentProps {
  dependentId: string;
  isCreate: boolean;
  onCancel: () => void;
  setDependentForm: (data: any) => void;
  setDependentFormValid: any;
  isViewMode: boolean;
  parentGroup: Group;
  primaryId: string;
}

export default function DependentsInformation({ readOnly }: { readOnly: boolean }) {
  const { getValues } = useFormContext();
  const {
    dependentId,
    setDependentId,
    isCreateDependent,
    setIsCreate,
    handleClear,
    setDependentForm,
    setDependentFormValid,
    isViewMode
  } = useMemberContext();

  const handleChooseDependent = useCallback((id) => setDependentId(id), [setDependentId]);
  const handleAddDependent = useCallback(() => setIsCreate(true), [setIsCreate]);

  if (dependentId || isCreateDependent) {
    return (
      <DependentMember
        dependentId={dependentId}
        onCancel={handleClear}
        isCreate={isCreateDependent}
        setDependentForm={setDependentForm}
        setDependentFormValid={setDependentFormValid}
        isViewMode={isViewMode}
        parentGroup={getValues('group')}
        primaryId={getValues('originMember.memberMasterMrn')}
      />
    );
  }

  return (
    <>
      {!readOnly && (
        <WithPermission requiredPerms={['MemberWrite']}>
          <div className="relative">
            <div className="inline absolute bottom-4 right-0">
              <AddButton onClick={handleAddDependent} label="Add dependent" />
            </div>
          </div>
        </WithPermission>
      )}
      <DependentTable onChoose={handleChooseDependent} data={getValues('relatedInstances')} />
    </>
  );
}

const DependentMember = ({
  dependentId,
  isCreate,
  onCancel,
  setDependentForm,
  setDependentFormValid,
  isViewMode,
  parentGroup,
  primaryId
}: DependentProps) => {
  const {
    member: { data }
  } = useMemberDetails({ memberId: dependentId, primaryId });
  const methods = useForm({
    mode: 'onChange',
    defaultValues: {
      ...data,
      readOnly: isViewMode || data?.readOnly,
      group: parentGroup
    },
    resolver: joiResolver(validationSchema)
  });

  useEffect(() => setDependentForm(methods), [methods, setDependentForm]);

  useEffect(() => {
    setDependentFormValid(methods.formState.isValid);
  }, [methods.formState.isValid, setDependentFormValid]);

  return (
    <form>
      <FormProvider {...methods}>
        <AccountInformation isCreate={isCreate} onBack={onCancel} />
      </FormProvider>
    </form>
  );
};
